import { useEffect, useState } from 'react'
import {
  MESH_COLOR_LABELS,
  resetMeshColors,
  setMeshColorAt,
  useMeshColors,
} from '../lib/mesh-colors'

/**
 * Floating colour picker for the mesh hero palette, shown only when the URL has
 * `?bg_color_picker=true`. Lets anyone live-tweak each colour in the mix to
 * explore palettes; it never persists and is gated behind the query param, so
 * it is invisible and inert for normal visitors. Client-only (reads the query
 * string after mount, so SSR and hydration render nothing).
 */
export function MeshColorPicker() {
  const [enabled, setEnabled] = useState(false)
  const colors = useMeshColors()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setEnabled(params.get('bg_color_picker') === 'true')
  }, [])

  if (!enabled) return null

  return (
    <div className="fixed bottom-4 right-4 z-[9999] w-60 rounded-2xl border border-ink-200 bg-white/95 p-4 shadow-xl backdrop-blur">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[12px] font-semibold text-ink-900">Mesh colours</span>
        <button
          type="button"
          onClick={resetMeshColors}
          className="text-[11px] font-medium text-ink-500 hover:text-ink-900"
        >
          Reset
        </button>
      </div>
      <div className="space-y-2.5">
        {colors.map((c, i) => (
          <label key={i} className="flex items-center gap-2.5">
            <input
              type="color"
              value={c}
              onChange={(e) => setMeshColorAt(i, e.target.value)}
              aria-label={MESH_COLOR_LABELS[i]}
              className="h-7 w-7 shrink-0 cursor-pointer rounded border border-ink-200 bg-transparent p-0"
            />
            <span className="flex-1 text-[11.5px] text-ink-600">{MESH_COLOR_LABELS[i]}</span>
            <span className="font-mono text-[10.5px] uppercase text-ink-400">{c}</span>
          </label>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void navigator.clipboard?.writeText(JSON.stringify(colors))}
        className="mt-3 w-full rounded-lg bg-ink-900 px-3 py-1.5 text-[11.5px] font-medium text-white hover:bg-ink-800"
      >
        Copy colours
      </button>
    </div>
  )
}
